import React from "react";
import {
  DataTable,
  DataTableHead,
  DataTableRow,
  DataTableCell as DataTableCellBase,
} from "@flixbus/honeycomb-react";
import type { LineOperationalData } from "../../graphql/queries";
import { monthLabels, monthOrder } from "@/constants/months";
import { formatNumber } from "@/utils/formatNumber";
import { metricLabels } from "./OperationalDataGrid.constants";
import styles from "./OperationalDataGrid.module.scss";

// React 19 forwardRef compatibility: cast to FC to satisfy JSX type constraints
const DataTableCell = DataTableCellBase as React.FC<any>;

const orderedMonthValues = (
  months: { month: string; value: number | null }[],
): (number | null)[] => {
  const byMonth = new Map(months.map((m) => [m.month, m.value]));
  return monthOrder.map((m) => byMonth.get(m) ?? null);
};

const OperationalDataGrid: React.FC<{ data: LineOperationalData }> = ({
  data,
}) => (
  <DataTable
    caption={`Operational data for ${data.line} / ${data.year}`}
    extraClasses={styles.grid}
    headers={
      <DataTableHead>
        <DataTableCell scope="col" colSpan={2} extraClasses={styles.labelCell}>
          Metric / Coach Type
        </DataTableCell>
        {monthLabels.map((month) => (
          <DataTableCell key={month} scope="col" align="right">
            {month}
          </DataTableCell>
        ))}
      </DataTableHead>
    }
  >
    {data.fields.flatMap((entry) =>
      entry.coachTypes.map((ctv, coachIndex) => (
        <DataTableRow key={`${entry.field}-${ctv.coachType}`}>
          {coachIndex === 0 && (
            <DataTableCell
              scope="row"
              rowSpan={entry.coachTypes.length}
              extraClasses={styles.labelCell}
            >
              {metricLabels[entry.field] ?? entry.field}
            </DataTableCell>
          )}
          <DataTableCell extraClasses={styles.coachCell}>
            {ctv.coachType}
          </DataTableCell>
          {orderedMonthValues(ctv.months).map((value, i) => (
            <DataTableCell key={monthOrder[i]} align="right">
              {formatNumber(value)}
            </DataTableCell>
          ))}
        </DataTableRow>
      )),
    )}
  </DataTable>
);

export default React.memo(OperationalDataGrid);
